import {
  Role,
  RoleKey,
  RoleNameMap,
  RoleNameToKeyMap,
  useCurrentWorkspace,
  useCustomMutation,
  useFindByQuery,
  useGrippContext,
  useWorkspace,
} from '@gripp/shared-logic';
import {
  CheckboxOption,
  CheckIcon,
  Colors,
  colors as CropCircleColors,
  tokens,
  XIcon,
} from '@gripp/shared-ui';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { ActivityIndicator, DataTable } from 'react-native-paper';
import { WorkspacesSection } from '../../../accounts/components/workspacesSection';
import { GET_ROLES } from '../../../accounts/operations/getRoles';

export const AccountPage = () => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace(false);
  const [value, setValue] = useState<string | null>(null);
  const { data } = useFindByQuery({
    modelName: 'role',
    query: GET_ROLES,
  });
  const dataWorkspace = useWorkspace(workspace?.id ?? '');
  const { logger } = useGrippContext();
  const { mutateAsync: changeDefaultRole, isPending: isUpdating } =
    useCustomMutation({
      query: CHANGE_DEFAULT_ROLE,
    });

  const defaultUserSetup = useMemo(
    () =>
      (data?.items ?? [])
        .filter((r: Role) => r.name !== RoleNameMap[RoleKey.GLOBAL_ADMIN])
        .map((r: Role) => {
          const key = RoleNameToKeyMap[r.name];
          return {
            label: t(`accountPage.types.${key}`),
            value: r.id,
          };
        }),
    [t, data]
  );

  const handleSelectType = async (value: string) => {
    const input = {
      workspaceId: dataWorkspace?.id,
      roleId: value,
    };
    changeDefaultRole(
      { input },
      {
        onSettled: async (data, error?: Error) => {
          if (error) {
            logger?.error(error, 'Failed to change default role');
            return;
          }
          setValue(value);
        },
      }
    );
  };

  useLayoutEffect(() => {
    const setInitialValue = async () => {
      if (!dataWorkspace) return;
      const role = dataWorkspace?.defaultRole
        ? data?.items.find(
            (r: Role) => r.name === dataWorkspace?.defaultRole?.name
          )
        : data?.items.find(
            (r: Role) => r.name === RoleNameMap[RoleKey.WORKSPACE_ADMIN]
          );
      setValue(role.id);
    };

    setInitialValue();
  }, [dataWorkspace, data]);

  const bulletList = [
    <Text><Text style={styles.infoBold}>{t('accountPage.types.admin')}</Text> – {t('accountPage.info.admin')}</Text>,
    <Text><Text style={styles.infoBold}>{t('accountPage.types.general')}</Text> – {t('accountPage.info.general')}</Text>,
    <Text><Text style={styles.infoBold}>{t('accountPage.types.restricted')}</Text> – {t('accountPage.info.restricted')}</Text>,
    <Text><Text style={styles.infoBold}>{t('accountPage.types.anonymous')}</Text> – {t('accountPage.info.anonymous')}</Text>,
  ];

  return (
    <View style={styles.container}>
      <View style={styles.tableEnvelopeContainer}>
        <View style={styles.tableContainer}>
          <DataTable>
            <DataTable.Header style={styles.tableHeader}>
              <DataTable.Title style={styles.tableTitle}>
                <Text style={styles.titleText}>
                  {t('accountPage.table.headers.accountType')}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={styles.tableTitle}>
                <Text style={styles.titleText}>
                  {t('accountPage.table.headers.accessActivity')}
                </Text>
              </DataTable.Title>
              <DataTable.Title style={styles.tableTitle}>
                <Text style={styles.titleText}>
                  {t('accountPage.table.headers.accessSettings')}
                </Text>
              </DataTable.Title>
            </DataTable.Header>
            <DataTable.Row style={styles.tableRow}>
              <DataTable.Cell>{t('accountPage.types.admin')}</DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <CheckIcon size={20} />
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <CheckIcon size={20} />
              </DataTable.Cell>
            </DataTable.Row>
            <DataTable.Row style={styles.tableRow}>
              <DataTable.Cell>{t('accountPage.types.general')}</DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <CheckIcon size={20} />
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                {t('accountPage.table.columns.generalToSettingsNote')}
              </DataTable.Cell>
            </DataTable.Row>
            <DataTable.Row style={styles.tableRow}>
              <DataTable.Cell>
                {t('accountPage.types.restricted')}
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                {t('accountPage.table.columns.restrictedToActivitiesNote')}
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <XIcon size={20} />
              </DataTable.Cell>
            </DataTable.Row>
            <DataTable.Row style={styles.tableRow}>
              <DataTable.Cell>
                {t('accountPage.types.anonymous')}
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <XIcon size={20} />
              </DataTable.Cell>
              <DataTable.Cell style={styles.tableCell}>
                <XIcon size={20} />
              </DataTable.Cell>
            </DataTable.Row>
          </DataTable>
        </View>
      </View>
      <View style={styles.infoContainer}>
        {
          bulletList.map((item, index) => {
            return(
              <View style={ styles.bulletRow } key={index}>
                <View style={ styles.bullet }>
                  <Text>{'\u2022 '}</Text>
                </View>
                <View style={ styles.bulletText }>
                  {item}
                </View>
              </View>
            );
          })
        }
      </View>
      <View style={styles.newUsersContainer}>
        <Text style={styles.sectionHeader}>
          {t('accountPage.header.newUsersSetup')}:
        </Text>
        <View style={styles.radioButtonsContainer}>
          {defaultUserSetup.map((option: { label: string; value: string }) => (
            <CheckboxOption
              key={option.value}
              mode="radio"
              state={option.value === value ? 'checked' : 'unchecked'}
              label={option.label}
              onPress={() => handleSelectType(option.value)}
              disabled={isUpdating}
            />
          ))}
          {isUpdating && (
            <ActivityIndicator
              color={Colors.primaryDark}
              size={20}
              style={styles.activityIndicator}
            />
          )}
        </View>
      </View>
      <WorkspacesSection />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    marginBottom: tokens.spacing['2xl'],
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.lg,
  },
  headerLabel: {
    ...tokens.typography.body.mediumEmphasised,
    letterSpacing: -0.25,
    marginBottom: tokens.spacing['2xl'],
  },
  sectionHeader: {
    ...tokens.typography.body.mediumEmphasised,
    marginBottom: tokens.spacing.md,
  },
  tableEnvelopeContainer: {
    maxHeight: 800,
    marginBottom: tokens.spacing['2xl'],
  },
  tableContainer: {
    flex: 1,
    borderRadius: tokens.borderRadius.sm,
    overflow: 'hidden',
    backgroundColor: tokens.colors.brand.white,
  },
  tableHeader: {
    backgroundColor: CropCircleColors.slate[100],
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderBottomWidth: 0,
  },
  tableTitle: {
    paddingVertical: 16,
    margin: 0,
  },
  tableRow: {
    cursor: 'auto',
    borderBottomWidth: 1,
    borderBottomColor: CropCircleColors.slate[100],
    minHeight: 52,
  },
  tableCell: {
    paddingLeft: 20,
    justifyContent: 'flex-start',
  },
  titleText: {
    ...tokens.typography.label.largeEmphasised,
    color: tokens.colors.neutral[800],
    letterSpacing: -0.2,
  },
  icon: {
    fontSize: 20,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flex: 1,
    marginVertical: 4
  },
  bullet: {
    width: 15
  },
  bulletText: {
    flex: 1
  },
  infoContainer: {
    marginLeft: tokens.spacing.xs,
    marginBottom: tokens.spacing['2xl'],
  },
  infoBold: {
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: tokens.colors.neutral[800],
  },
  newUsersContainer: {
    marginBottom: tokens.spacing['2xl'],
  },
  radioButtonsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    flexWrap: 'wrap',
  },
  radioButtonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CropCircleColors.slate[100],
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    width: '100%',
  },
  radioButtonLabel: {
    ...tokens.typography.label.large,
    marginRight: tokens.spacing.sm,
    flex: 1,
  },
  activityIndicator: {
    marginTop: 24,
  },
});

const CHANGE_DEFAULT_ROLE = `
  mutation ChangeRoleWorkspace($input: ChangeRoleWorkspaceInput!) {
    workspace {
        changeDefaultRole(input: $input) {
          workspaceId
        }
    }
  }
`;
